import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Stack,
} from "@mui/material";

const ChangeLogTable = ({
  changeLog,
  convertFirstLetterToCapital,
  role,
  handlePageClick,
  pageItemCount,
  currentPage,
}) => {
  const renderBlockStatus = (value) =>
    value ? (
      <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
        {value}
      </Typography>
    ) : (
      <Typography variant="caption" align="center">
        -
      </Typography>
    );

  const renderVisitName = (item) =>
    item.visit_name ||
    (parseInt(item.visit_number) === 0 && "Baseline Visit") ||
    (parseInt(item.visit_number) === -3 && "Screening Visit") ||
    (parseInt(item.visit_number) === -4 && "Other ECRFs") ||
    (parseInt(item.visit_number) === -1 && "Adverse Event") ||
    (parseInt(item.visit_number) === -2 && "Unscheduled Event") ||
    (item.visit_number && `Visit ${item.visit_number}`) ||
    "-";

  const renderRole = (item) =>
    item.role === "Inv_coordinator"
      ? "CRC"
      : item.role === "Cro_coordinator"
        ? "Sub CRO"
        : item.role === "Sub_investigator"
          ? "Sub INV"
          : item.role === "Investigator"
            ? "INV"
            : item.role || "-";

  const showPatientInfo =
    role !== "CRO Admin" && role !== "Cro_coordinator" && role !== "Data Manager";

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const emptyColumns = changeLog?.empty_columns || [];

  const columns = [
    {
      field: "site_participant_id",
      header: "SiteID – SubjectsID",
      cell: (item) => item.site_participant_id,
    },
    { field: "visit_name", header: "Visit Name", align: "center", cell: renderVisitName },
    {
      field: "block_name",
      header: "Block Name",
      cell: (item) => convertFirstLetterToCapital(item.block_name),
    },
    showPatientInfo && {
      field: "patient_first_name",
      header: "First Name",
      cell: (item) => convertFirstLetterToCapital(item.patient_first_name),
    },
    showPatientInfo && {
      field: "patient_last_name",
      header: "Last Name",
      cell: (item) => convertFirstLetterToCapital(item.patient_last_name),
    },
    showPatientInfo && {
      field: "patient_email",
      header: "Email",
      cell: (item) => item.patient_email,
    },
    {
      field: "description",
      header: "Description",
      cell: (item) => convertFirstLetterToCapital(item.description),
    },
    {
      field: "new_data",
      header: "New Data",
      cell: (item) => convertFirstLetterToCapital(item.new_data),
    },
    {
      field: "previous_data",
      header: "Previous Data",
      cell: (item) => convertFirstLetterToCapital(item.previous_data),
    },
    { field: "trial_name", header: "Trial", cell: (item) => item.trial_name },
    { field: "updated_by_email", header: "Updater Email", cell: (item) => item.updated_by_email },
    {
      field: "updated_by_first_name",
      header: "Updater First Name",
      cell: (item) => item.updated_by_first_name,
    },
    {
      field: "updated_by_last_name",
      header: "Updater Last Name",
      cell: (item) => item.updated_by_last_name,
    },
    {
      field: "created_at",
      header: "Date / Time",
      cell: (item) => (
        <>
          {new Date(item.created_at + "Z").toDateString()}
          <br />
          {new Date(item.created_at + "Z").toLocaleTimeString()}
        </>
      ),
    },
    { field: "role", header: "Role", cell: renderRole },
    {
      field: "has_constraint_error",
      header: "Out of Range",
      cell: (item) => (item.has_constraint_error ? "Yes" : "No"),
    },
    {
      field: "is_completed",
      header: "Completed",
      cell: (item) => (item.is_completed ? "Yes" : "No"),
    },
    {
      field: "cro_dm_cdqc_block_status_previous",
      header: "CRO/DM/CDQC Block Status (Previous)",
      cellSx: { p: 1 },
      cell: (item) => renderBlockStatus(item.cro_dm_cdqc_block_status_previous),
    },
    {
      field: "cro_dm_cdqc_block_status_new",
      header: "CRO/DM/CDQC Block Status (New)",
      cellSx: { p: 1 },
      cell: (item) => renderBlockStatus(item.cro_dm_cdqc_block_status_new),
    },
    {
      field: "inv_esignature_previous",
      header: "INV eSignature (Previous)",
      cellSx: { p: 1 },
      cell: (item) => renderBlockStatus(item.inv_esignature_previous),
    },
    {
      field: "inv_esignature_new",
      header: "INV eSignature (New)",
      cellSx: { p: 1 },
      cell: (item) => renderBlockStatus(item.inv_esignature_new),
    },
    {
      field: "dm_ecrf_freeze_previous",
      header: "DM eCRF Freeze (Previous)",
      cellSx: { p: 1 },
      cell: (item) => renderBlockStatus(item.dm_ecrf_freeze_previous),
    },
    {
      field: "dm_ecrf_freeze_new",
      header: "DM eCRF Freeze (New)",
      cellSx: { p: 1 },
      cell: (item) => renderBlockStatus(item.dm_ecrf_freeze_new),
    },
  ].filter((column) => column && !emptyColumns.includes(column.field));

  return (
    <Box sx={{ py: 2 }}>
      <TableContainer component={Paper} sx={{ maxHeight: "80vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.field} sx={headerCellSx}>
                  {column.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {changeLog?.results?.map((item, index) => (
              <TableRow
                key={index}
                hover
                data-testid="change-log-row"
                data-block-name={item.block_name}
                data-log-description={item.description}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.align}
                    sx={column.cellSx}
                    data-field={column.field}
                  >
                    {column.cell(item)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
        <Pagination
          color="primary"
          page={currentPage + 1}
          count={Math.ceil(changeLog?.count / pageItemCount)}
          onChange={(_, page) => handlePageClick({ selected: page - 1 })}
        />
      </Stack>
    </Box>
  );
};

export default ChangeLogTable;
